import type { Exercise } from "../types/exercises";
import type { SavedWord } from "../types/words";
import { shuffle } from "./text";

/** Builds a short lesson from the learner's own saved words. Returns null if there aren't enough. */
export function buildPracticeLesson(words: SavedWord[]): Exercise[] | null {
  const pool = words.slice(0, 10);
  if (pool.length < 3) return null;

  const matchPairs = pool.slice(0, 5).map((w) => ({
    term: w.term,
    translation: w.translation,
    fromTranslator: w.source === "translator",
  }));
  const exercises: Exercise[] = [{ id: "pr-match", type: "match-words", pairs: matchPairs }];

  const audioWord = pool[0];
  const distractors = shuffle(pool.slice(1).map((w) => w.translation)).slice(0, 3);
  if (distractors.length >= 2) {
    exercises.push({
      id: "pr-audio",
      type: "match-audio",
      fromTranslator: audioWord.source === "translator",
      audio: audioWord.term,
      options: shuffle([audioWord.translation, ...distractors]),
      answer: audioWord.translation,
    });
  }

  const memoryPairs = pool.slice(0, Math.min(6, pool.length)).map((w) => ({
    term: w.term,
    translation: w.translation,
    fromTranslator: w.source === "translator",
  }));
  if (memoryPairs.length >= 3) {
    exercises.push({ id: "pr-memory", type: "memory", pairs: memoryPairs });
  }

  const phrase = pool.find((w) => w.term.includes(" ")) ?? pool[0];
  exercises.push({ id: "pr-say", type: "pronounce", phrase: phrase.term, translation: phrase.translation });

  return exercises;
}

export function solutionFor(ex: Exercise): string {
  switch (ex.type) {
    case "match-words":
    case "memory":
      return ex.pairs.map((p) => `${p.term} = ${p.translation}`).join(" · ");
    case "match-picture":
      return ex.promptTranslation;
    case "match-audio":
      return `“${ex.audio}” = ${ex.answer}`;
    case "fill-gap":
      return `${ex.before} ${ex.answer} ${ex.after}`;
    case "pronounce":
      return ex.phrase;
    case "rearrange":
      return ex.answer.join(" ");
  }
}

export function termsIn(ex: Exercise): string[] {
  switch (ex.type) {
    case "match-words":
    case "memory":
      return ex.pairs.map((p) => p.term);
    case "match-picture":
      return [ex.prompt];
    case "match-audio":
      return [ex.audio];
    case "pronounce":
      return [ex.phrase];
    default:
      return [];
  }
}

export function translatorItemsIn(ex: Exercise): number {
  if (ex.type === "match-words" || ex.type === "memory") {
    return ex.pairs.filter((p) => p.fromTranslator).length;
  }
  return ex.fromTranslator ? 1 : 0;
}
